// What role each component of a product plays: a front, the carcase, an
// internal fitting or hardware. Templates come from the server as XML and say
// so only through flags on each operation group, every one a string, and the
// catalog changes without code changes. So the rules are one ordered table,
// and a component no rule recognises is unknown, which still counts as solid:
// an unfamiliar component keeps the geometry it has always had rather than
// disappearing from what blocks or lines up.
import * as THREE from 'three';
import {OperationGroup} from 'components/customer/Preview3D/types';
import {isDoorOrDrawerFront} from 'components/customer/RoomPlanner/helpers/doorFronts';
import {isAdjustableLeg} from 'components/customer/RoomPlanner/helpers/calculations';

export type ComponentRole =
    'front' | 'carcase' | 'internal' | 'hardware' | 'unknown';

/**
 * An operation group as the template XML reader leaves it: every attribute
 * copied across, as a string.
 */
export type TemplateOperationGroup = Partial<OperationGroup> &
    Record<string, unknown>;

/**
 * @param {unknown} value A flag from the template.
 * @return {boolean} True for true or "true" in any case. The reader copies
 * attributes as strings, so "false" must not count.
 */
const isSet = (value: unknown): boolean =>
    value === true || String(value).toLowerCase() === 'true';

/**
 * @param {TemplateOperationGroup} group An operation group.
 * @param {string[]} flags Flag names.
 * @return {boolean} True when any of them is set.
 */
const anySet = (group: TemplateOperationGroup, flags: string[]) =>
    flags.some((flag) =>
        // eslint-disable-next-line security/detect-object-injection -- flag names are constants from COMPONENT_ROLE_RULES
        isSet(group[flag])
    );

const CORNER_DRAWER_FLAGS = ['IsCornerLeft', 'IsCornerRight'];

/**
 * The rules, first match wins. Supporting a new kind of component is a row
 * here, with a row in componentRoles.test.ts beside it.
 */
export const COMPONENT_ROLE_RULES: {
    role: Exclude<ComponentRole, 'unknown'>;
    reason: string;
    matches: (group: TemplateOperationGroup) => boolean;
}[] = [
    {
        role: 'front',
        reason: 'a door or drawer front',
        matches: (group) => isDoorOrDrawerFront(group),
    },
    {
        role: 'front',
        // Flagged only as a corner drawer part with an exterior finish, which
        // no front test catches.
        reason: 'the face of an L-shape drawer',
        matches: (group) =>
            anySet(group, CORNER_DRAWER_FLAGS) && isSet(group.ExteriorMaterial),
    },
    {
        role: 'hardware',
        reason: 'a drawer runner',
        matches: (group) => isSet(group.IsDrawerRunner),
    },
    {
        role: 'internal',
        reason: 'part of a drawer box',
        matches: (group) =>
            anySet(group, [
                'IsDrawerBottom',
                'IsDrawerBack',
                'IsBinWithInnerDrawer',
                ...CORNER_DRAWER_FLAGS,
            ]),
    },
    {
        role: 'internal',
        reason: 'a shelf or partition',
        matches: (group) =>
            anySet(group, ['IsShelf', 'IsShelfVertical', 'IsShelfPartition']) ||
            Boolean(group.ShelfLabel),
    },
    {
        role: 'carcase',
        reason: 'finished in the carcase material',
        matches: (group) => isSet(group.CarcaseMaterial),
    },
];

/**
 * @param {TemplateOperationGroup} group An operation group from a template.
 * @return {object} Its role, and the rule that gave it.
 */
export const roleOfOperationGroup = (
    group: TemplateOperationGroup
): {role: ComponentRole; reason: string} => {
    const rule = COMPONENT_ROLE_RULES.find(({matches}) => matches(group));

    return rule
        ? {role: rule.role, reason: rule.reason}
        : {role: 'unknown', reason: 'no rule matches'};
};

/**
 * @param {THREE.Object3D} component A component of a product.
 * @return {ComponentRole} Its role, as tagged on it or on a group above it
 * when the scene was built. Components built outside the template loop are
 * read by what they are: an adjustable leg is hardware, a shaker door a
 * front, anything else unknown.
 */
export const componentRoleOf = (component: THREE.Object3D): ComponentRole => {
    if (isAdjustableLeg(component)) return 'hardware';

    for (
        let node: THREE.Object3D | null = component;
        node;
        node = node.parent
    ) {
        const {componentRole, isDoorOrDrawerFront: isFront} = node.userData;
        if (componentRole) return componentRole as ComponentRole;
        if (isFront) return 'front';
    }

    return 'unknown';
};

/**
 * @param {ComponentRole} role A component's role.
 * @return {boolean} True when the component is part of the product's solid
 * body: a front, the carcase, or a component no rule recognises.
 */
export const isSolidRole = (role: ComponentRole): boolean =>
    role !== 'internal' && role !== 'hardware';
